"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.allWorkspacePlans = exports.allUserPlans = exports.allPlans = exports.enterprise = exports.organizationManaged = exports.organizationPaid = exports.organizationFree = exports.organizationTest = exports.legacyUltraPlan = exports.legacyProPlan = exports.legacyEarlySupporterPlan = exports.legacyEducationalPlan = exports.legacyFreePlan = exports.proPlanVariantFrom202504 = exports.proPlan = exports.corePlan = exports.starterPlanVariantFrom202504 = exports.starterPlan = exports.educationalPlan = exports.pufTrialPlan = exports.trialIndividualPlanVariantFrom202504 = exports.trialIndividualPlan = exports.noPlan = void 0;
const models_1 = require("@buildwithflux/models");
const context_1 = require("../functions/context");
const migration_1 = require("../functions/migration");
const planEpoch_1 = require("../functions/planEpoch");
const subscription_1 = require("../functions/subscription");
const trial_1 = require("../functions/trial");
const plans_1 = require("../models/plans");
/*
 * ==== Base and special plans
 */
exports.noPlan = {
    key: plans_1.PlanKey.parse("no_plan"),
    contextType: "user",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.userNoPlan,
        max_private_projects: 0,
        edit_private_projects: false,
        copilot_free_credits: models_1.CreditAmount.parse(0),
        copilot_can_use_paid_credits: false,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(0),
    },
    subscription: () => undefined,
    matches: (context) => (0, context_1.contextHasNewPlanEpoch)(context),
    visibleToUsers: false,
    status: () => models_1.SubscriptionStatus.enum.active,
};
/**
 * This plan matches any sort of trial, reverse or PUF
 */
exports.trialIndividualPlan = {
    key: plans_1.PlanKey.parse("trial_individual"),
    contextType: "user",
    entitlements: {
        // Does not participate in category entitlement, but should have most other entitlements similar to proPlan
        export_basic_formats: true,
        import_altium: true,
        import_edif: true,
        copilot_free_credits: models_1.CreditAmount.parse(150),
        copilot_can_use_paid_credits: false, // No, because _reverse_ trials need not have given a payment method
        copilot_default_spending_limit: models_1.SpendingLimit.parse(0),
        max_private_projects: "infinity",
        edit_private_projects: true,
    },
    subscription(context) {
        return (0, trial_1.getTrialInfoFromUserPrivateMetadata)(context.currentUserPrivateMetadata, context.stripeClientMode)
            ?.subscription;
    },
    matches(context) {
        return !!(0, trial_1.getTrialInfoFromUserPrivateMetadata)(context.currentUserPrivateMetadata, context.stripeClientMode);
    },
    visibleToUsers: true,
    status: (context) => (0, trial_1.getSyntheticSubscriptionStatusOfTrialSubscription)((0, trial_1.getTrialInfoFromUserPrivateMetadata)(context.currentUserPrivateMetadata, context.stripeClientMode)),
};
/**
 * A variant of the trialIndividualPlan in which the user gets more credits, to match the increased pro plan, when
 * the user has planEpoch=from_202410 and planVariant=from_202504
 */
exports.trialIndividualPlanVariantFrom202504 = {
    key: plans_1.PlanKey.parse("trial_individual_from_202504"),
    contextType: "user",
    entitlements: {
        copilot_free_credits: models_1.CreditAmount.parse(500),
        copilot_paid_credit_price_type: "discount",
    },
    subscription(context) {
        const variant = (0, planEpoch_1.getPlanVariantFromUserPrivateMetadata)(context.currentUserPrivateMetadata);
        if (variant !== models_1.PlanVariantForEpochFrom202410.enum.from_202504) {
            return undefined;
        }
        return exports.trialIndividualPlan.subscription(context);
    },
    matches(context) {
        const variant = (0, planEpoch_1.getPlanVariantFromUserPrivateMetadata)(context.currentUserPrivateMetadata);
        if (variant !== models_1.PlanVariantForEpochFrom202410.enum.from_202504) {
            return false;
        }
        return exports.trialIndividualPlan.matches(context);
    },
    visibleToUsers: false,
    status: (context) => exports.trialIndividualPlan.status(context),
};
/**
 * Plan that only matches PUF trials, to give them extra entitlements over the other 50% of the current trial
 * experiment (reverse)
 *
 * This plan is given to users who are either still trialing their PUF trial, or have converted to a paid plan
 */
exports.pufTrialPlan = {
    key: plans_1.PlanKey.parse("trial_individual_puf"),
    contextType: "user",
    entitlements: {
        pcb_auto_layout: true,
    },
    subscription: (context) => {
        if (exports.proPlan.matches(context)) {
            return exports.proPlan.subscription(context);
        }
        if (exports.starterPlan.matches(context)) {
            return exports.starterPlan.subscription(context);
        }
        if (exports.trialIndividualPlan.matches(context)) {
            return exports.trialIndividualPlan.subscription(context);
        }
        return (0, subscription_1.getSubscriptionForProductFromContext)(context, "user", models_1.PaymentPlanCategory.enum.userPro);
    },
    matches: (context) => {
        const trialType = (0, trial_1.getTrialTypeFromUserPrivateMetadata)(context.currentUserPrivateMetadata);
        if (trialType !== models_1.TrialType.enum.payment_details_up_front) {
            return false;
        }
        return exports.starterPlan.matches(context) || exports.proPlan.matches(context) || exports.trialIndividualPlan.matches(context);
    },
    visibleToUsers: false,
    status: (context) => {
        return ((0, subscription_1.mostActiveSubscriptionStatus)([exports.starterPlan, exports.proPlan, exports.trialIndividualPlan].map((plan) => plan.status(context))) ?? models_1.SubscriptionStatus.enum.canceled);
    },
};
/*
 * ==== New plans
 */
/**
 * The exact entitlements of this plan are not decided formally yet, and it's not active
 * (current edu users are all legacyEducationalPlan described below)
 *
 * @deprecated This plan is not actively used and was written presumptively before finalizing
 * the new epoch billing system. Educational users will receive discount coupons for regular
 * plans instead of a separate educational plan. This plan is kept for backward compatibility
 * but should not be used for new users.
 */
exports.educationalPlan = {
    key: plans_1.PlanKey.parse("educational"),
    contextType: "user",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.userEdu,
        export_basic_formats: true,
        max_private_projects: "infinity",
        copilot_free_credits: models_1.CreditAmount.parse(100),
        copilot_can_use_paid_credits: false,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(0),
        edit_private_projects: true,
    },
    matches: (context) => !!context.currentUserPrivateMetadata.plans?.isEducational,
    visibleToUsers: true,
    status: () => models_1.SubscriptionStatus.enum.active,
    subscription: () => undefined,
};
exports.starterPlan = {
    key: plans_1.PlanKey.parse("starter"),
    contextType: "user",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.userStarter,
        export_basic_formats: true,
        max_private_projects: 50,
        edit_private_projects: true,
        copilot_free_credits: models_1.CreditAmount.parse(50),
        copilot_can_use_paid_credits: true,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(50_00),
        max_editors: 0,
    },
    subscription(context) {
        return (0, subscription_1.getSubscriptionForProductFromContext)(context, "user", models_1.PaymentPlanCategory.enum.userStarter);
    },
    matches(context) {
        return !!this.subscription(context);
    },
    visibleToUsers: true,
    status(context) {
        return this.subscription(context)?.status ?? models_1.SubscriptionStatus.enum.canceled;
    },
};
exports.starterPlanVariantFrom202504 = {
    key: plans_1.PlanKey.parse("starter_from_202504"),
    contextType: "user",
    entitlements: {
        copilot_free_credits: models_1.CreditAmount.parse(100),
    },
    subscription(context) {
        const subscription = exports.starterPlan.subscription(context);
        if (!subscription) {
            return undefined;
        }
        const variant = (0, planEpoch_1.getPlanVariantFromUserPrivateMetadata)(context.currentUserPrivateMetadata);
        if (variant !== models_1.PlanVariantForEpochFrom202410.enum.from_202504) {
            return undefined;
        }
        return subscription;
    },
    matches(context) {
        return !!this.subscription(context);
    },
    visibleToUsers: false,
    status(context) {
        return this.subscription(context)?.status ?? models_1.SubscriptionStatus.enum.canceled;
    },
};
exports.corePlan = {
    key: plans_1.PlanKey.parse("core"),
    contextType: "user",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.userCore,
        export_basic_formats: true,
        import_altium: true,
        import_edif: true,
        max_private_projects: "infinity",
        edit_private_projects: true,
        copilot_free_credits: models_1.CreditAmount.parse(100),
        copilot_can_use_paid_credits: true,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(50_00),
        max_editors: 20,
    },
    subscription(context) {
        return (0, subscription_1.getSubscriptionForProductFromContext)(context, "user", models_1.PaymentPlanCategory.enum.userCore);
    },
    matches(context) {
        return !!this.subscription(context);
    },
    visibleToUsers: true,
    status(context) {
        return this.subscription(context)?.status ?? models_1.SubscriptionStatus.enum.canceled;
    },
};
exports.proPlan = {
    key: plans_1.PlanKey.parse("pro"),
    contextType: "user",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.userPro,
        export_basic_formats: true,
        import_altium: true,
        import_edif: true,
        max_private_projects: "infinity",
        edit_private_projects: true,
        copilot_free_credits: models_1.CreditAmount.parse(150),
        copilot_can_use_paid_credits: true,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(50_00),
        max_editors: 20,
    },
    subscription(context) {
        return (0, subscription_1.getSubscriptionForProductFromContext)(context, "user", models_1.PaymentPlanCategory.enum.userPro);
    },
    matches(context) {
        return !!this.subscription(context);
    },
    visibleToUsers: true,
    status(context) {
        return this.subscription(context)?.status ?? models_1.SubscriptionStatus.enum.canceled;
    },
};
/**
 * A variant of the pro plan in which the user gets more credits and a discount
 */
exports.proPlanVariantFrom202504 = {
    key: plans_1.PlanKey.parse("pro_from_202504"),
    contextType: "user",
    entitlements: {
        copilot_free_credits: models_1.CreditAmount.parse(500),
        copilot_paid_credit_price_type: "discount",
    },
    subscription(context) {
        const subscription = exports.proPlan.subscription(context);
        if (!subscription) {
            return undefined;
        }
        const variant = (0, planEpoch_1.getPlanVariantFromUserPrivateMetadata)(context.currentUserPrivateMetadata);
        if (variant !== models_1.PlanVariantForEpochFrom202410.enum.from_202504) {
            return undefined;
        }
        return subscription;
    },
    matches(context) {
        return !!this.subscription(context);
    },
    visibleToUsers: true,
    status(context) {
        return this.subscription(context)?.status ?? models_1.SubscriptionStatus.enum.canceled;
    },
};
/*
 * ==== Legacy plans
 */
exports.legacyFreePlan = {
    key: plans_1.PlanKey.parse("legacy_free"),
    contextType: "user",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.userLegacyFree,
        export_basic_formats: true,
        max_private_projects: 5,
        copilot_free_credits: models_1.CreditAmount.parse(0),
        copilot_can_use_paid_credits: false,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(0),
        edit_private_projects: true,
    },
    subscription: () => undefined,
    matches: (context) => (0, context_1.contextHasLegacyPlanEpoch)(context) || (0, migration_1.contextIsInMigrationGracePeriodFromLegacyPlanEpoch)(context),
    visibleToUsers: false,
    status() {
        return models_1.SubscriptionStatus.enum.active;
    },
};
exports.legacyEducationalPlan = {
    key: plans_1.PlanKey.parse("legacy_educational"),
    contextType: "user",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.userLegacyEdu,
        export_basic_formats: true,
        max_private_projects: "infinity",
        copilot_free_credits: models_1.CreditAmount.parse(100),
        edit_private_projects: true,
    },
    subscription: () => undefined,
    matches: (context) => context.currentUser.activeProductUid === models_1.PaymentPlanUids.userLegacyEdu, // TODO: https://linear.app/buildwithflux/issue/FLUX-3220/move-new-educational-plan-matching-away-from-activeproductuid
    visibleToUsers: true,
    status() {
        return models_1.SubscriptionStatus.enum.active;
    },
};
exports.legacyEarlySupporterPlan = {
    key: plans_1.PlanKey.parse("legacy_early_supporter"),
    contextType: "user",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.userLegacyEarly,
        export_basic_formats: true,
        copilot_free_credits: models_1.CreditAmount.parse(100),
        copilot_can_use_paid_credits: true,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(50_00),
        max_private_projects: "infinity",
        edit_private_projects: true,
    },
    subscription(context) {
        return (0, subscription_1.getSubscriptionForProductFromContext)(context, "user", models_1.PaymentPlanCategory.enum.userLegacyEarly);
    },
    matches(context) {
        return !!this.subscription(context);
    },
    visibleToUsers: true,
    status(context) {
        return this.subscription(context)?.status ?? models_1.SubscriptionStatus.enum.canceled;
    },
};
exports.legacyProPlan = {
    key: plans_1.PlanKey.parse("legacy_pro"),
    contextType: "user",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.userLegacyPro,
        export_basic_formats: true,
        copilot_free_credits: models_1.CreditAmount.parse(100),
        copilot_can_use_paid_credits: true,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(50_00),
        max_private_projects: "infinity",
        edit_private_projects: true,
    },
    subscription(context) {
        return (0, subscription_1.getSubscriptionForProductFromContext)(context, "user", models_1.PaymentPlanCategory.enum.userLegacyPro);
    },
    matches(context) {
        return !!this.subscription(context);
    },
    visibleToUsers: true,
    status(context) {
        return this.subscription(context)?.status ?? models_1.SubscriptionStatus.enum.canceled;
    },
};
exports.legacyUltraPlan = {
    key: plans_1.PlanKey.parse("legacy_ultra"),
    contextType: "user",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.userLegacyUltra,
        export_basic_formats: true,
        import_altium: true,
        import_edif: true,
        copilot_free_credits: models_1.CreditAmount.parse(500),
        copilot_can_use_paid_credits: true,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(50_00),
        max_private_projects: "infinity",
        edit_private_projects: true,
    },
    subscription(context) {
        return (0, subscription_1.getSubscriptionForProductFromContext)(context, "user", models_1.PaymentPlanCategory.enum.userLegacyUltra);
    },
    matches(context) {
        return !!this.subscription(context);
    },
    visibleToUsers: true,
    status(context) {
        return this.subscription(context)?.status ?? models_1.SubscriptionStatus.enum.canceled;
    },
};
/*
 * ==== Test organization plan
 *
 * Special plan for test organizations used in Cypress tests
 */
exports.organizationTest = {
    key: plans_1.PlanKey.parse("organization_test"),
    contextType: "organization",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.organizationPaid,
        edit_private_projects: true,
        export_basic_formats: true,
        copilot_free_credits: models_1.CreditAmount.parse(500),
        copilot_can_use_paid_credits: true,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(1000_00),
        import_altium: true,
        import_edif: true,
        max_private_projects: "infinity",
    },
    subscription: () => undefined,
    matches: (context) => context.type === "organization" && context.membership.organization.activeProductUid === "test",
    visibleToUsers: false,
    status() {
        return models_1.SubscriptionStatus.enum.active;
    },
};
/*
 * ==== Legacy organization plans
 *
 * It's important to understand that in order to find the entitlements for an organization, the user needs access to the
 * organizationPrivateMetadata, implying that you can only receive entitlements from an organization if you are a member
 * of that organization.
 */
exports.organizationFree = {
    key: plans_1.PlanKey.parse("legacy_organization_free"),
    contextType: "organization",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.organizationFree,
        edit_private_projects: true,
        export_basic_formats: true,
        copilot_free_credits: models_1.CreditAmount.parse(0),
        copilot_can_use_paid_credits: false,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(0),
        max_private_projects: 0,
    },
    subscription: () => undefined,
    matches: (context) => context.type === "organization",
    visibleToUsers: true,
    status() {
        return models_1.SubscriptionStatus.enum.active;
    },
};
exports.organizationPaid = {
    key: plans_1.PlanKey.parse("legacy_organization_paid"),
    contextType: "organization",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.organizationPaid,
        edit_private_projects: true,
        export_basic_formats: true,
        copilot_free_credits: models_1.CreditAmount.parse(500),
        copilot_can_use_paid_credits: true,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(1000_00),
        import_altium: true,
        import_edif: true,
        max_private_projects: "infinity",
    },
    subscription(context) {
        return (0, subscription_1.getSubscriptionForProductFromContext)(context, "organization", models_1.PaymentPlanCategory.enum.organizationPaid);
    },
    matches(context) {
        return context.type === "organization" && !!this.subscription(context);
    },
    visibleToUsers: true,
    status(context) {
        return this.subscription(context)?.status ?? models_1.SubscriptionStatus.enum.canceled;
    },
};
exports.organizationManaged = {
    key: plans_1.PlanKey.parse("organization_managed"),
    contextType: "organization",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.organizationManaged,
        edit_private_projects: true,
        export_basic_formats: true,
        copilot_free_credits: models_1.CreditAmount.parse(0),
        copilot_can_use_paid_credits: true,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(0),
        import_altium: true,
        import_edif: true,
        max_private_projects: "infinity",
    },
    subscription(context) {
        return (0, subscription_1.getSubscriptionForProductFromContext)(context, "organization", models_1.PaymentPlanCategory.enum.organizationManaged);
    },
    matches(context) {
        return context.type === "organization" && !!this.subscription(context);
    },
    visibleToUsers: true,
    status(context) {
        const subscription = this.subscription(context);
        if (!subscription)
            return models_1.SubscriptionStatus.enum.canceled;
        if (subscription.status === models_1.SubscriptionStatus.enum.canceled) {
            return models_1.SubscriptionStatus.enum.canceled;
        }
        return models_1.SubscriptionStatus.enum.active;
    },
};
/*
 * === Enterprises
 */
exports.enterprise = {
    key: plans_1.PlanKey.parse("enterprise"),
    contextType: "enterprise",
    entitlements: {
        category: models_1.PaymentPlanCategory.enum.enterprise,
        edit_private_projects: true,
        export_basic_formats: true,
        // free copilot credits are managed via custom sales process and static credit allocations
        copilot_free_credits: models_1.CreditAmount.parse(0),
        copilot_can_use_paid_credits: true,
        copilot_default_spending_limit: models_1.SpendingLimit.parse(1000_00),
        import_altium: true,
        import_edif: true,
        max_private_projects: "infinity",
    },
    subscription: () => undefined,
    matches: (context) => context.type === "enterprise",
    visibleToUsers: true,
    status() {
        return models_1.SubscriptionStatus.enum.active;
    },
};
/**
 * Remember to add your new plan to this array
 *
 * The ordering of this array is used in one case: it determines the ordering of the `contributingPlans` array in the
 * entitlement result. We generally find the "most important" contributing plan at the end of this array.
 */
exports.allPlans = [
    exports.noPlan,
    exports.trialIndividualPlan,
    exports.trialIndividualPlanVariantFrom202504,
    exports.pufTrialPlan,
    exports.proPlan,
    exports.proPlanVariantFrom202504,
    exports.corePlan,
    exports.starterPlan,
    exports.starterPlanVariantFrom202504,
    exports.educationalPlan,
    exports.legacyFreePlan,
    exports.legacyEducationalPlan,
    exports.legacyEarlySupporterPlan,
    exports.legacyProPlan,
    exports.legacyUltraPlan,
    exports.organizationTest,
    exports.organizationFree,
    exports.organizationPaid,
    exports.organizationManaged,
    exports.enterprise,
];
exports.allUserPlans = exports.allPlans.filter((plan) => plan.contextType === "user");
exports.allWorkspacePlans = exports.allPlans.filter((plan) => plan.contextType === "organization" || plan.contextType === "enterprise");
//# sourceMappingURL=plans.js.map